import { check_audio } from "./_submissionCheck.js";
import type { Submission, Word } from "./backendTypes.js";
import { classifySketch } from "./inference_img.js";
import { transcribe } from "./transcriberEn.js";
const DB_NAME: string = "vocabDB";
export const DB_VERSION: number = 5;

let dbInstance: IDBDatabase | null = null;

export const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }
    const request: IDBOpenDBRequest = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      const target = event.target as IDBOpenDBRequest;
      console.error(`Database error: ${target.error}`);
      reject(`Database error: ${target.error}`);
    };

    request.onsuccess = (event) => {
      const target = event.target as IDBOpenDBRequest;
      dbInstance = target.result;
      console.log("Database opened successfully.");
      localStorage.setItem("dbVersion", DB_VERSION.toString());
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const target = event.target as IDBOpenDBRequest;
      const db = target.result as IDBDatabase;
      console.log("Upgrading database...");

      if (!db.objectStoreNames.contains("student")) {
        db.createObjectStore("student", {
          keyPath: "studentId",
          autoIncrement: true,
        });
        console.log("created student object store");
      }
      if (!db.objectStoreNames.contains("word")) {
        db.createObjectStore("word", {
          keyPath: "english",
        });
        console.log("created word object store");
      }
      if (!db.objectStoreNames.contains("student_learning")) {
        const learningDB = db.createObjectStore("student_learning", {
          keyPath: "learningId",
          autoIncrement: true,
        });
        console.log("created student learning object store");
        learningDB.createIndex("studentId", "studentId", { unique: false });
        console.log("created studentId index in learning object store");
      }

      if (!db.objectStoreNames.contains("class")) {
        db.createObjectStore("class", {
          keyPath: "classId",
          autoIncrement: true,
        });
        console.log("created class object store");
      }
      if (!db.objectStoreNames.contains("model")) {
        db.createObjectStore("model", {
          keyPath: "modelUse",
        });
        console.log("created class object store");
      }
    };
  });
};

export const saveData = async <T>(
  data: T,
  storeName: string
): Promise<string> => {
  if (!dbInstance) await openDB();

  return new Promise((resolve, reject) => {
    const transaction = (dbInstance as IDBDatabase).transaction(
      [storeName],
      "readwrite"
    );
    const store: IDBObjectStore = transaction.objectStore(storeName);
    const request: IDBRequest = store.put(data);

    request.onsuccess = () => {
      console.log("Data saved successfully");
      resolve("Data saved successfully");
    };

    request.onerror = (event) => {
      const target = event.target as IDBRequest;
      console.error(`Error saving data: ${target.error}`);
      reject(`Error saving data: ${target.error?.message}`);
    };
  });
};

export const getData = async <T>(storeName: string): Promise<T | undefined> => {
  if (!dbInstance) await openDB();

  return new Promise((resolve, reject) => {
    const transaction = (dbInstance as IDBDatabase).transaction(
      [storeName],
      "readonly"
    );

    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = (event) => {
      const target = event.target as IDBRequest;
      console.log(`Data retrieved: ${target.result}`);
      resolve(target.result as T | undefined);
    };

    request.onerror = (event) => {
      const target = event.target as IDBRequest;
      console.log(`Error retrieving data: ${target.error}`);
      reject(`Error retrieving data: ${target.error?.message}`);
    };
  });
};
export const getBlobEntry = async (id: string): Promise<Blob | null> => {
  if (!dbInstance) await openDB();

  return new Promise((resolve, reject) => {
    const transaction = (dbInstance as IDBDatabase).transaction(
      ["model"],
      "readonly"
    );
    const store = transaction.objectStore("model");

    const request = store.get(id);

    request.onsuccess = () => {
      const result = request.result;
      resolve(result?.modelFile);
    };
    request.onerror = (e) => {
      const target = e.target as IDBRequest;
      reject(
        new Error(
          `Error retrieving blob with ID ${id}: ${target.error?.message}`
        )
      );
    };
  });
};
export const clearStore = async (storeName: string): Promise<string> => {
  if (!dbInstance) await openDB();

  return new Promise((resolve, reject) => {
    const transaction = (dbInstance as IDBDatabase).transaction(
      [storeName],
      "readwrite"
    );

    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => {
      console.log("Store cleared successfully");
      resolve("Store cleared successfully");
    };

    request.onerror = (event) => {
      const target = event.target as IDBRequest;
      console.log(`Error clearing store: ${target.error}`);
      reject(`Error clearing store: ${target.error?.message}`);
    };
  });
};

export const storeWords = async (words: Array<Word>): Promise<string> => {
  if (!dbInstance) await openDB();

  // Fetch all blobs first
  const preparedWords = await Promise.all(
    words.map(async (word) => {
      try {
        const imageBlob = await fetchAsBlob(word.image_url);
        console.log("downloaded image, starting audio");
        const audio_telugu = await fetchAsBlob(word.audio_telugu_url);
        console.log("downloaded audio, starting audio");
        const audio_english = await fetchAsBlob(word.audio_english_url);
        console.log("downloaded all");

        console.log("Retrieved image and audio files for word.");

        return {
          ...word,
          image: imageBlob,
          audio_telugu: audio_telugu,
          audio_english: audio_english,
        };
      } catch (error) {
        console.log(`Failed to fetch resources for ${word.english}:`, error);
        throw new Error(
          `Error fetching resources for ${word.english}: ${error}`
        );
      }
    })
  );

  console.log(preparedWords);
  return new Promise((resolve, reject) => {
    const transaction = (dbInstance as IDBDatabase).transaction(
      ["word"],
      "readwrite"
    );

    for (const word of preparedWords) {
      console.log(`trying to store ${word.english} in database`);
      const store = transaction.objectStore("word");

      transaction.oncomplete = () => {
        console.log("Transaction to store word completed successfully.");
        resolve("Word stored successfully");
      };

      transaction.onerror = (error) => {
        const target = error.target as IDBTransaction;
        console.log(`Error on transaction to store words: ${target.error}`);
        reject(`Transaction error: ${target.error?.message}`);
      };
      const request = store.put(word);
      request.onsuccess = () => {
        console.log("put word in indexeddb");
      };
      request.onerror = (error) => {
        const target = error.target as IDBRequest;
        console.log(`Skipped word ${word.english}: ${target.error?.message}`);
      };
    }
  });
};

// Fetch image from GCS url
const fetchAsBlob = async (url: string) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
  return await response.blob();
};

type Class = {
  grade: "I" | "II" | "III";
  className: "A" | "B" | "C" | "D" | "E" | "F";
};

type Student = {
  studentName: "string";
  classId: number;
};

// Return list of all classes
export const getAllClasses = async (): Promise<Class> => {
  if (!dbInstance) await openDB();
  return new Promise((resolve, reject) => {
    const transaction = (dbInstance as IDBDatabase).transaction(
      ["class"],
      "readonly"
    );
    const store = transaction.objectStore("class");
    const request = store.getAll();

    request.onerror = (e) => {
      const target = e.target as IDBRequest;
      console.log(`Error while getting all classes: ${target.error?.message}`);
      reject(`Error while getting all classes: ${target.error?.message}`);
    };

    // TODO: fix type in resolve
    request.onsuccess = (e) => {
      const target = e.target as IDBRequest;
      console.log("Got all existing classes from database.");
      resolve(target.result);
    };
  });
};

// Get all students for a class
export const getStudentProfiles = async (class_id: number) => {
  if (!dbInstance) await openDB();

  return new Promise((resolve, reject) => {
    const transaction = (dbInstance as IDBDatabase).transaction(
      ["student"],
      "readonly"
    );

    const store = transaction.objectStore("student");
    const request = store.getAll();

    request.onerror = (e) => {
      const target = e.target as IDBRequest;
      console.log(
        `Error while retrieving students for class_id ${class_id}: ${target.error?.message}`
      );
      reject(
        `Error while retrieving students for class_id ${class_id}: ${target.error?.message}`
      );
    };

    // TODO: fix types
    request.onsuccess = (e) => {
      const target = e.target as IDBRequest;
      console.log(`Got all student profiles for class ${class_id}`);
      resolve(target.result);
    };
  });
};

// Return list of next words for a student to practice
// TODO: return a mix of old and new words
export const getNextWords = async (studentId: number, count = 10) => {
  if (!dbInstance) await openDB();

  const transaction = (dbInstance as IDBDatabase).transaction(
    ["word", "student_learning"],
    "readonly"
  );

  const learningStore = transaction.objectStore("student_learning");
  const wordStore = transaction.objectStore("word");

  // 1. Get all words the student has already seen
  // TODO: add types and index for studentId
  const seenWords:string[] = await new Promise((res, rej) => {
    const index = learningStore.index("studentId");
    const request = index.getAll(studentId);
    request.onsuccess = () => res(request.result.map((entry) => entry.wordEnglish));
    request.onerror = () => {
      console.log(
        `Error while finding all words learned by a student: ${request.error}`
      );
      rej(request.error);
    };
  });

  // 2. Get all words from the word store
  const allWords: Word[]= await new Promise((res, rej) => {
    const request = wordStore.getAll();
    request.onsuccess = () => res(request.result);
    request.onerror = () => {
      console.log(`Error while getting all words: ${request.error}`);
      rej(request.error);
    };
  });

  // 3. Filter out seen words
  const unseenWords = allWords.filter(
    (word: Word) => !seenWords.includes(word.english)
  );

  // 4. Shuffle and pick `count` words
  // TODO: change order the words get picked in
  const shuffled = unseenWords.sort(() => 0.5 - Math.random());
  const selected = shuffled.slice(0, Math.min(count, shuffled.length));

  // TODO: append 10 random seenWords to selected

  return selected;
};

// Checks the student's audio or image using AI models
// TODO: check types
export const submit = async (submission: Submission): Promise<boolean> => {
  if (!dbInstance) await openDB();

  // TODO: check for each type with different models

  let learning = submission;

  if (submission.skip) {
    learning = { ...submission, correct: false };
    return false;
  }
  let correct = false;

  switch (submission.type) {
    case "audio_english": {
      const resultEng = await transcribe(
        submission.file,
        submission.wordEnglish
      );
      console.log(resultEng);
      correct = resultEng.isCorrect;
      break;
    }
    case "audio_telugu":
      correct = await check_audio(
        audio_telugu_model,
        submission.file,
        submission.wordEnglish
      );
      break;
    case "image": {
      const onnxModelBlob = await getBlobEntry("image");
      const result = await classifySketch(
        onnxModelBlob, // Model blob from database
        submission.file, // Image blob from browser
        submission.wordEnglish // Target word
      );
      console.log(result);
      correct = result.isMatch;
    }
  }

  learning = { ...submission, correct: correct };

  const transaction = (dbInstance as IDBDatabase).transaction(
    ["student_learning"],
    "readwrite"
  );
  const learningStore = transaction.objectStore("student_learning");
  const request = learningStore.add(learning);

  request.onerror = (error) => {
    const target = error.target as IDBRequest;
    console.log(
      `Error while updating db with learning progress: ${target.error?.message}`
    );
  };

  request.onsuccess = () => {
    console.log("Updated learning progress.");
  };

  return correct;
};

// Return student's learning progress using studentId
// TODO: types
export const studentLearningProgress = async (
  studentId: number
): Promise<LearningProgress> => {
  if (!dbInstance) await openDB();

  const transaction = (dbInstance as IDBDatabase).transaction(
    ["student"],
    "readonly"
  );
  const studentStore = transaction.objectStore("student");
  const stdData: Student = await new Promise((res, rej) => {
    const nameRequest = studentStore.get(studentId);
    nameRequest.onerror = () => rej(nameRequest.error);
    nameRequest.onsuccess = () => res(nameRequest.result);
  });

  const studentName = stdData.studentName;

  return new Promise((res, rej) => {
    const transaction = (dbInstance as IDBDatabase).transaction(
      ["student_learning"],
      "readonly"
    );
    const learningStore = transaction.objectStore("student_learning");
    const index = learningStore.index("studentId");
    const request = index.getAll(studentId);

    request.onsuccess = () => {
      const studentData: Submission[] = request.result;
      const progress: LearningProgress = {
        studentId: studentId,
        studentName: studentName,
        wordsSeen: 0,
        wordsAttempted: 0,
        wordsSkipped: 0,
        wordsCorrect: 0,
      };
      const trackWords: Set<string> = new Set();
      for (const submission of studentData) {
        if (submission.skip) progress.wordsSkipped++;
        else progress.wordsAttempted++;

        if (!trackWords.has(submission.wordEnglish)) {
          trackWords.add(submission.wordEnglish);
          progress.wordsSeen++;
        }

        if (submission.correct) {
          progress.wordsCorrect++;
        }
      }
      res(progress);
    };
    request.onerror = () => rej(request.error);
  });
};

type LearningProgress = {
  studentId: number;
  studentName: string;
  wordsSeen: number;
  wordsSkipped: number;
  wordsAttempted: number;
  wordsCorrect: number;
};
